import { Ionicons } from '@expo/vector-icons';
import React, { type ReactNode } from 'react';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';
import { View } from 'react-native';

import { ThemedText } from '@/components/themed-text';
import { PressableScale } from '@/components/ui/pressable-scale';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/utils/cn';
import { useThemedHex } from '@/hooks/use-theme-colors';

const PRIMARY = '#FD5001';

interface FieldBlockProps {
  title: string;
  /** Consequência da escolha atual, curta, na mesma linha do título. */
  note?: string;
  error?: string;
  children: ReactNode;
}

/**
 * Cabeçalho de campo: pergunta à esquerda, consequência à direita, controle
 * embaixo — e nada depois dele.
 *
 * O empilhamento antigo (rótulo em caixa alta, pills, parágrafo) fazia o olho
 * descer três vezes para entender uma escolha só, e o parágrafo final quase
 * sempre repetia o que as opções já diziam.
 */
export function FieldBlock({ title, note, error, children }: FieldBlockProps) {
  return (
    <View className="gap-2.5 py-1">
      <View className="flex-row items-baseline justify-between gap-3">
        <ThemedText className="font-semibold text-[15px] leading-5 text-neutral-900">
          {title}
        </ThemedText>
        {error || note ? (
          <ThemedText
            numberOfLines={1}
            className={cn(
              'shrink text-right text-xs',
              error ? 'text-error-600' : 'text-neutral-400',
            )}
          >
            {error ?? note}
          </ThemedText>
        ) : null}
      </View>
      {children}
    </View>
  );
}

/**
 * Atalho para o teclado: os valores prontos são pills, e digitar precisa de uma
 * pill também — senão o campo grande fica parecendo só um rótulo.
 */
export function OtherChip({
  active,
  onPress,
  testID,
}: {
  active: boolean;
  onPress: () => void;
  testID?: string;
}) {
  const th = useThemedHex();
  return (
    <PressableScale
      haptic
      accessibilityRole="button"
      accessibilityLabel="Digitar outro valor"
      accessibilityState={{ selected: active }}
      onPress={onPress}
      testID={testID}
      className={cn(
        'h-9 flex-row items-center gap-1.5 rounded-full px-3.5',
        active ? 'bg-primary' : 'bg-neutral-100',
      )}
      style={{ borderCurve: 'continuous' }}
    >
      <Ionicons name="create-outline" size={14} color={active ? '#FFFFFF' : th('#525252')} />
      <ThemedText
        className={cn(
          'font-semibold text-[13px]',
          active ? 'text-primary-foreground' : 'text-neutral-700',
        )}
      >
        Outro
      </ThemedText>
    </PressableScale>
  );
}

export interface RowOption<T> {
  value: T;
  label: string;
  /** O que essa opção faz — no lugar do parágrafo genérico do grupo. */
  meaning?: string;
}

interface OptionRowsProps<TForm extends FieldValues, TValue> {
  control: Control<TForm>;
  name: Path<TForm>;
  title: string;
  note?: string;
  options: RowOption<TValue>[];
  testID?: string;
}

/**
 * Escolha única em linhas: cada opção carrega o próprio significado, então a
 * explicação morre junto com a alternativa que não foi escolhida.
 */
export function OptionRows<TValue>({
  title,
  note,
  error,
  options,
  value,
  onChange,
  testID,
}: {
  title: string;
  note?: string;
  error?: string;
  options: RowOption<TValue>[];
  value: TValue | undefined;
  onChange: (value: TValue) => void;
  testID?: string;
}) {
  return (
    <FieldBlock title={title} note={note} error={error}>
      <View
        className="overflow-hidden rounded-xl border border-neutral-900/5"
        style={{ borderCurve: 'continuous' }}
      >
        {options.map((option, index) => {
          const selected = option.value === value;
          return (
            <PressableScale
              key={String(option.value)}
              haptic
              accessibilityRole="radio"
              accessibilityState={{ selected }}
              onPress={() => onChange(option.value)}
              testID={testID ? `${testID}-${String(option.value)}` : undefined}
              className={cn(
                'flex-row items-center gap-3 px-3.5 py-3',
                index > 0 ? 'border-t border-neutral-100' : '',
                selected ? 'bg-primary/5' : 'bg-card',
              )}
            >
              <View className="flex-1 gap-0.5">
                <ThemedText
                  className={cn(
                    'text-sm leading-5',
                    selected ? 'font-semibold text-neutral-900' : 'text-neutral-700',
                  )}
                >
                  {option.label}
                </ThemedText>
                {option.meaning ? (
                  <ThemedText className="text-xs leading-4 text-neutral-500">
                    {option.meaning}
                  </ThemedText>
                ) : null}
              </View>
              {selected ? (
                <Ionicons name="checkmark-circle" size={20} color={PRIMARY} />
              ) : (
                <View className="h-5 w-5 rounded-full border-2 border-neutral-200" />
              )}
            </PressableScale>
          );
        })}
      </View>
    </FieldBlock>
  );
}

/** `OptionRows` ligado ao react-hook-form. */
export function ControlledOptionRows<TForm extends FieldValues, TValue>({
  control,
  name,
  title,
  note,
  options,
  testID,
}: OptionRowsProps<TForm, TValue>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <OptionRows
          title={title}
          note={note}
          error={fieldState.error?.message}
          options={options}
          value={field.value as TValue}
          onChange={field.onChange}
          testID={testID}
        />
      )}
    />
  );
}

interface SwitchRowProps<TForm extends FieldValues> {
  control: Control<TForm>;
  name: Path<TForm>;
  title: string;
  /** O que muda ao desligar, em uma linha. */
  note?: string;
  /** Efeito colateral da troca (ex.: limpar o campo que o switch governa). */
  onChange?: (checked: boolean) => void;
  testID?: string;
}

/** Liga/desliga na mesma tipografia dos outros campos do assistente. */
export function ControlledSwitchRow<TForm extends FieldValues>({
  control,
  name,
  title,
  note,
  onChange,
  testID,
}: SwitchRowProps<TForm>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <SwitchRow
          title={title}
          note={note}
          checked={!!field.value}
          onChange={(checked) => {
            field.onChange(checked);
            onChange?.(checked);
          }}
          testID={testID}
        />
      )}
    />
  );
}

interface SwitchRowRawProps {
  title: string;
  note?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  testID?: string;
}

/** Mesma linha, para estados que não moram num booleano do formulário. */
export function SwitchRow({ title, note, checked, onChange, testID }: SwitchRowRawProps) {
  return (
    <View className="flex-row items-center gap-3 py-1.5">
      <View className="flex-1 gap-0.5">
        <ThemedText className="font-semibold text-[15px] leading-5 text-neutral-900">
          {title}
        </ThemedText>
        {note ? (
          <ThemedText className="text-xs leading-4 text-neutral-500">{note}</ThemedText>
        ) : null}
      </View>
      <Switch checked={checked} onCheckedChange={onChange} testID={testID} />
    </View>
  );
}

interface StepperRowProps<TForm extends FieldValues> {
  control: Control<TForm>;
  name: Path<TForm>;
  title: string;
  /** Leitura do valor atual em português, ao lado do título. */
  note?: string;
  min?: number;
  max?: number;
  testID?: string;
}

/** Contador na própria linha do rótulo: título e leitura à esquerda, − / + à direita. */
export function ControlledStepperRow<TForm extends FieldValues>({
  control,
  name,
  title,
  note,
  min = 1,
  max = 99,
  testID,
}: StepperRowProps<TForm>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        // Campo em branco é campo em branco: o assistente não responde por
        // ninguém, então o primeiro "+" é que escolhe o mínimo.
        const empty = field.value == null;
        const value = empty ? min : Number(field.value);
        return (
          <View className="flex-row items-center gap-3 py-1.5">
            <View className="flex-1 gap-0.5">
              <ThemedText className="font-semibold text-[15px] leading-5 text-neutral-900">
                {title}
              </ThemedText>
              {fieldState.error?.message ? (
                <ThemedText className="text-xs leading-4 text-error-600">
                  {fieldState.error.message}
                </ThemedText>
              ) : note ? (
                <ThemedText className="text-xs leading-4 text-neutral-500">{note}</ThemedText>
              ) : null}
            </View>

            <View className="flex-row items-center gap-2">
              <PressableScale
                haptic
                accessibilityRole="button"
                accessibilityLabel="Diminuir"
                disabled={empty || value <= min}
                onPress={() => field.onChange(Math.max(min, value - 1))}
                testID={testID ? `${testID}-minus` : undefined}
                className="h-10 w-10 items-center justify-center rounded-full bg-neutral-100"
                style={{ opacity: empty || value <= min ? 0.4 : 1 }}
              >
                <ThemedText className="font-bold text-lg text-neutral-800">−</ThemedText>
              </PressableScale>
              <ThemedText
                className={cn(
                  'min-w-8 text-center font-bold text-lg',
                  empty ? 'text-neutral-300' : 'text-neutral-900',
                )}
              >
                {empty ? '—' : value}
              </ThemedText>
              <PressableScale
                haptic
                accessibilityRole="button"
                accessibilityLabel="Aumentar"
                disabled={!empty && value >= max}
                onPress={() => field.onChange(empty ? min : Math.min(max, value + 1))}
                testID={testID ? `${testID}-plus` : undefined}
                className="h-10 w-10 items-center justify-center rounded-full bg-neutral-100"
                style={{ opacity: !empty && value >= max ? 0.4 : 1 }}
              >
                <ThemedText className="font-bold text-lg text-neutral-800">+</ThemedText>
              </PressableScale>
            </View>
          </View>
        );
      }}
    />
  );
}
